import React, { useState, useEffect } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

const EventsTable = ({ events, onEventClick }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showPassedEvents, setShowPassedEvents] = useState(false);
  const [creationFilter, setCreationFilter] = useState("all");
  const [eventMonth, setEventMonth] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "asc" });

  const now = new Date();

  useEffect(() => {
    console.log("🔍 Debugging Events Data:", events);
  }, [events]);

  const formatDateKey = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return "";
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}`;
  };

  const eventMonthOptions = Array.from(
    new Set(events.map((e) => formatDateKey(e.eventStartDate)).filter(Boolean))
  ).sort();

  const filteredEvents = events.filter((event) => {
    const eventDate = event.eventStartDate ? new Date(event.eventStartDate) : null;
    const createdDate = event.createdAt ? new Date(event.createdAt) : null;

    // ❌ Hide past events if unchecked
    if (!showPassedEvents && eventDate && eventDate < now) return false;

    // 📅 Filter by creation date
    if (creationFilter !== "all" && createdDate) {
      const daysDiff = (now - createdDate) / (1000 * 60 * 60 * 24);
      if (
        (creationFilter === "24h" && daysDiff > 1) ||
        (creationFilter === "3d" && daysDiff > 3) ||
        (creationFilter === "7d" && daysDiff > 7)
      ) {
        return false;
      }
    }

    if (eventMonth) {
      const key = formatDateKey(event.eventStartDate);
      if (key !== eventMonth) return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      event.title.toLowerCase().includes(searchLower) ||
      (event.eventStartDate && event.eventStartDate.toLowerCase().includes(searchLower)) ||
      (event.createdAt && event.createdAt.toLowerCase().includes(searchLower))
    );
  });

  // 📊 **Sort Events**
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const { key, direction } = sortConfig;
    const valueA = a[key];
    const valueB = b[key];

    if (!valueA || !valueB) return 0;

    if (typeof valueA === "string" && typeof valueB === "string") {
      return direction === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    }

    if (!isNaN(valueA) && !isNaN(valueB)) {
      return direction === "asc" ? valueA - valueB : valueB - valueA;
    }

    return 0;
  });

  // 🔀 **Handle Sorting Click**
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // 🖱️ **Handle Row Click**
const handleRowClick = (event) => {
  if (event.shareIdentifier) {
    onEventClick(event.shareIdentifier); // ✅ Notify parent component
  } else {
    console.warn("❌ No Share Identifier found for this event:", event);
  }
};

  const totalRevenue = filteredEvents.reduce((sum, ev) => sum + Number(ev.totalrevenue || 0), 0);
  const totalTicketsSold = filteredEvents.reduce((sum, ev) => sum + Number(ev.ticketssold || 0), 0);

  return (
    <div style={styles.container}>
      {/* 🔍 Search & Filters */}
      <div style={{ marginBottom: 12, color: "#90caf9", fontWeight: "bold" }}>
        🎟️ Total Tickets Sold: {totalTicketsSold} | 💰 Total Revenue: {totalRevenue.toFixed(2)} NIS
      </div>

      {/* 🔍 Filters */}
      <div style={styles.filterContainer}>
        <TextField
          label="Search Events"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchBox}
          InputProps={{ style: { color: "#fff" } }}
          InputLabelProps={{ style: { color: "#bbb" } }}
        />

        {/* 📅 Creation Date Filter */}
        <FormControl size="small" style={styles.filterSelect}>
          <InputLabel style={{ color: "#bbb" }}>Created In</InputLabel>
          <Select
            value={creationFilter}
            onChange={(e) => setCreationFilter(e.target.value)}
            style={{ background: "#333", color: "#fff" }}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="24h">Last 24h</MenuItem>
            <MenuItem value="3d">Last 3 Days</MenuItem>
            <MenuItem value="7d">Last Week</MenuItem>
          </Select>
        </FormControl>

        {/* 📆 Month Filter */}
        <FormControl size="small" style={styles.filterSelect}>
          <InputLabel style={{ color: "#bbb" }}>Event Month</InputLabel>
          <Select
            value={eventMonth}
            onChange={(e) => setEventMonth(e.target.value)}
            style={{ background: "#333", color: "#fff" }}
          >
            <MenuItem value="">All Months</MenuItem>
            {eventMonthOptions.map((m) => (
              <MenuItem key={m} value={m}>
                {new Date(`${m}-01`).toLocaleString("default", { month: "long", year: "numeric" })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ✅ Show Passed Events */}
        <FormControlLabel
          control={
            <Checkbox
              checked={showPassedEvents}
              onChange={() => setShowPassedEvents(!showPassedEvents)}
              style={{ color: "#90caf9" }}
            />
          }
          label="Show Passed Events"
          style={{ color: "#fff" }}
        />
      </div>

      {/* 📊 Events Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th} onClick={() => handleSort("title")}>
                Title {sortConfig.key === "title" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th style={styles.th} onClick={() => handleSort("eventStartDate")}>
                Event Date {sortConfig.key === "eventStartDate" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th style={styles.th} onClick={() => handleSort("createdAt")}>
                Created {sortConfig.key === "createdAt" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th style={styles.th} onClick={() => handleSort("ticketssold")}>
                Ticket Sold {sortConfig.key === "ticketssold" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th style={styles.th} onClick={() => handleSort("totalrevenue")}>
                Total Revenue {sortConfig.key === "totalrevenue" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEvents.length > 0 ? (
              sortedEvents.map((event, index) => (
                <tr
                  key={event.shareIdentifier || index} // Ensure unique key
                  style={styles.tr}
                  onClick={() => handleRowClick(event)} // Click navigation
                >
                  <td style={styles.td}>{event.title}</td>
                  <td style={styles.td}>
                    {event.eventStartDate ? new Date(event.eventStartDate).toLocaleDateString() : "N/A"}
                  </td>
                  <td style={styles.td}>
                    {event.createdAt ? new Date(event.createdAt).toLocaleDateString() : "N/A"}
                  </td>
                  <td style={styles.td}>{event.ticketssold ?? 0}</td>
                  <td style={styles.td}>{Number(event.totalrevenue || 0).toFixed(2)} NIS</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ ...styles.td, textAlign: "center" }}>
                  No events found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 🎨 **Styles**
const styles = {
  container: {
    backgroundColor: "#1e1e1e",
    color: "#fff",
    padding: "16px",
    border: "1px solid #444",
    borderRadius: "8px",
    maxWidth: "95vw",
  },
  filterContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
    flexWrap: "wrap",
    gap: "12px",
  },
  searchBox: {
    flex: 1,
    background: "#333",
    borderRadius: "4px",
  },
  filterSelect: {
    minWidth: "180px",
  },
  tableWrapper: {
    maxHeight: "500px",
    overflowY: "auto",
    borderRadius: "8px",
    paddingRight: "8px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    color: "#fff",
  },
  tableHeader: {
    backgroundColor: "#333",
    borderBottom: "2px solid #fff",
    cursor: "pointer",
  },
  td: {
    padding: "10px",
    borderBottom: "1px solid #888",
  },
};

export default EventsTable;
